import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { X } from 'lucide-react'
import toast from 'react-hot-toast'

import { AWXCardStatus } from '@ankor-io/common/types/awx'
import type { Card } from '@ankor-io/common/types/cards'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@/ui-components/Dialog'

type UnfreezeCardModalProps = {
  onClose: () => void
  isOpen: boolean
  cardId: string
  onSuccess?: (updatedCard: Card) => void
}

export const UnfreezeCardModal = ({ onClose, isOpen = false, cardId, onSuccess }: UnfreezeCardModalProps) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const handleUnfreezeCard = () => {
    const unfreezeCard = async () => {
      if (!activeOrganizationId || !activeConnectedAccountId) {
        throw new Error('Missing organization or connected account context')
      }

      const res = await fetch(
        `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/card/${cardId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await getAccessToken()}`,
          },
          body: JSON.stringify({
            status: AWXCardStatus.ACTIVE,
          }),
        },
      )

      if (!res.ok) {
        const errorText = await res.text()
        throw new Error(`Failed to unfreeze card: ${res.status} ${res.statusText} - ${errorText}`)
      }

      // Get the updated card data
      const updatedCard: Card = await res.json()

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(updatedCard)
      }

      // Close the modal after successful unfreeze
      onClose()
    }

    toast.promise(unfreezeCard, {
      loading: 'Unfreezing card...',
      success: 'Card unfrozen successfully',
      error: 'Failed to unfreeze card',
    })
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>You're about to unfreeze this card</span>
          <Button
            plain
            onClick={onClose}
            aria-label="Close"
          >
            <X data-slot="icon" />
          </Button>
        </div>
      </DialogTitle>
      <DialogDescription>
        Once unfrozen, the card will be available for transactions again. The card will be immediately active and can be
        used for new purchases.
        <br />
        <br />
        You can freeze the card again at any time if needed.
      </DialogDescription>
      <DialogActions>
        <Button
          color="primary"
          onClick={handleUnfreezeCard}
        >
          Confirm to unfreeze
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UnfreezeCardModal
